import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MessageSquare, ShieldCheck } from "lucide-react";

const TOPICS = [
  { key: "security", label: "Security alerts" },
  { key: "billing_critical", label: "Critical billing alerts" },
  { key: "integration_critical", label: "Critical integration alerts" },
  { key: "storefront_campaign", label: "Storefront and campaign updates" },
];

const errMsg = (e, fallback) => e?.response?.data?.detail?.message || fallback;

function PhoneSection({ state, onChange }) {
  const p = state.preferences;
  const [phone, setPhone] = useState(p.phone_number || "");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const start = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.post("/merchant/sms/phone/start", { phone });
      setSent(true);
      setMsg({ ok: true, text: "We sent a verification code to your phone." });
    } catch (e) { setMsg({ ok: false, text: errMsg(e, "Could not send a verification code.") }); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/merchant/sms/phone/verify", { phone, code });
      onChange(r.data); setSent(false); setCode("");
      setMsg({ ok: true, text: "Your number is verified." });
    } catch (e) { setMsg({ ok: false, text: errMsg(e, "Invalid or expired code.") }); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    setMsg(null);
    try { const r = await api.delete("/merchant/sms/phone"); onChange(r.data); setPhone(""); }
    catch (e) { setMsg({ ok: false, text: errMsg(e, "Could not remove your number.") }); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4">
      <label htmlFor="sms-phone" className="text-sm font-medium text-slate-800">Mobile number</label>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        <input id="sms-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 416 555 1234" aria-describedby="sms-phone-help" data-testid="sms-phone-input"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-56" />
        <span data-testid="sms-phone-status"
          className={`text-xs px-2 py-1 rounded-full ${p.phone_verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          {p.phone_verified ? "Verified" : "Not Verified"}
        </span>
        <button type="button" onClick={start} disabled={busy || !phone} data-testid="sms-verify-number-btn"
          className="pf-btn pf-btn-primary text-sm px-3 py-2 rounded-lg disabled:opacity-50">Verify Number</button>
        {p.phone_verified && (
          <button type="button" onClick={remove} disabled={busy} data-testid="sms-remove-number-btn"
            className="text-sm text-slate-600 underline">Remove number</button>
        )}
      </div>
      <p id="sms-phone-help" className="text-xs text-slate-500 mt-1">Canadian phone numbers only</p>
      {sent && (
        <div className="flex items-center gap-2 mt-3">
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={10}
            aria-label="Verification code" placeholder="6-digit code" data-testid="sms-code-input"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-36" />
          <button type="button" onClick={verify} disabled={busy || code.length < 4} data-testid="sms-confirm-code-btn"
            className="pf-btn pf-btn-primary text-sm px-3 py-2 rounded-lg disabled:opacity-50">Confirm code</button>
        </div>
      )}
      {msg && <p role="status" data-testid="sms-phone-message"
        className={`text-xs mt-2 ${msg.ok ? "text-emerald-600" : "text-rose-600"}`}>{msg.text}</p>}
    </div>
  );
}

function PreferencesSection({ state, onChange }) {
  const p = state.preferences;
  const [error, setError] = useState(null);
  const setTopic = async (key, value) => {
    setError(null);
    try { const r = await api.patch("/merchant/sms/preferences", { topics: { [key]: value } }); onChange(r.data); }
    catch { setError("Could not update preference."); }
  };
  const setMarketing = async (value) => {
    setError(null);
    try { const r = await api.post("/merchant/sms/marketing", { consent: value }); onChange(r.data); }
    catch (e) { setError(errMsg(e, "Could not update promotional SMS.")); }
  };
  return (
    <fieldset className="mt-5">
      <legend className="text-sm font-medium text-slate-800">Notification preferences</legend>
      <div className="mt-2 space-y-2">
        {TOPICS.map((t) => (
          <label key={t.key} className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="w-4 h-4" checked={!!p.topics[t.key]}
              onChange={(e) => setTopic(t.key, e.target.checked)} data-testid={`sms-topic-${t.key}`} />
            {t.label}
          </label>
        ))}
        <label className="flex items-start gap-2 text-sm text-slate-700 pt-2 border-t border-slate-100">
          <input type="checkbox" className="w-4 h-4 mt-0.5" checked={!!p.marketing_consent}
            disabled={!p.phone_verified} onChange={(e) => setMarketing(e.target.checked)}
            data-testid="sms-marketing-checkbox" />
          <span><span className="font-medium">Promotional SMS (optional).</span> {p.marketing_wording}</span>
        </label>
        {!p.phone_verified && <p className="text-xs text-slate-500">Verify a Canadian number to opt in to promotional SMS.</p>}
      </div>
      {error && <p className="text-xs text-rose-600 mt-2" data-testid="sms-prefs-error">{error}</p>}
    </fieldset>
  );
}

export function SmsSettingsCard() {
  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    api.get("/merchant/sms/preferences").then((r) => setState(r.data))
      .catch(() => setLoadError("Could not load your SMS settings."));
  }, []);

  return (
    <div className="pf-card mt-6" data-testid="settings-sms-card">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center">
          <MessageSquare className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-display text-lg font-medium tracking-tight text-slate-900">SMS notifications</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md">
            Optional. Add a Canadian mobile number for important security, billing and integration alerts.
            Sign-in and account emails keep coming to your business email.
          </p>
        </div>
      </div>
      {!state && !loadError && <p className="text-xs text-slate-500 mt-3" data-testid="sms-loading">Loading SMS settings…</p>}
      {loadError && <p className="text-xs text-rose-600 mt-3">{loadError}</p>}
      {state && !state.sms_available && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-4 flex items-center gap-2" data-testid="sms-unavailable-note">
          <ShieldCheck className="w-4 h-4" /> SMS delivery isn't switched on yet. Your preferences are saved for when it is.
        </p>
      )}
      {state && <PhoneSection state={state} onChange={setState} />}
      {state && <PreferencesSection state={state} onChange={setState} />}
    </div>
  );
}
